import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  TextInput,
  StyleSheet,
} from "react-native";
import Text from "@components/CustomText";
import color from "@constants/color";
import LocalityCard from "../components/LocalityCard";
import CrateCard from "../components/CrateCard";
import CustomerCard from "../components/CustomerCard";
import MisrouteSheet from "../components/MisrouteSheet";
import { tokens, sharedStyles } from "../styles";

// Locality / Crate / Customer sub-toggle inside the second top-level tab.
// Uses small pill controls (matches prototype's sub-tab feel) instead of
// another underlined TabBar to keep it visually subordinate.

const SUB_TABS = [
  { key: "DC", label: "Locality" },
  { key: "CRATE", label: "Crate" },
  { key: "TP", label: "Customer" },
];

const LocalityCrateInfoTab = ({
  data,
  loading,
  reload,
  submitting,
  submit,
  cpId,
  onPressViewMap,
}) => {
  const [active, setActive] = useState("DC");
  const [misroute, setMisroute] = useState({ visible: false, card: null });
  // Customer-tab-only search. ~200 entries on a typical route, so client-
  // side substring filter is fast enough — no debounce needed. Reset on
  // sub-tab change for a clean state.
  const [customerSearch, setCustomerSearch] = useState("");

  const list = useMemo(
    () => (Array.isArray(data?.data?.[active]) ? data.data[active] : []),
    [data, active],
  );
  // Pass the full cp_dbs list down to the locality + crate cards so they
  // can widen "ASSIGNED TO" beyond customer-assignment-derived DBs. A
  // locality can subscribe to multiple DBs via cp_db_routes_v2 even when
  // no customer is currently assigned on every one.
  const cpDbs = useMemo(
    () => (Array.isArray(data?.cp_dbs) ? data.cp_dbs : []),
    [data],
  );

  // Customer-tab search: substring match across customer name, title
  // (which includes crate ids + locality), address, locality, and the
  // resolved assigned DB name. Resolution mirrors the same fallback
  // CustomerCard uses so the search matches what the user sees.
  const filteredCustomers = useMemo(() => {
    if (active !== "TP") return list;
    const q = customerSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((card) => {
      const ud = (card?.tp_list || [])[0] || {};
      const resolvedDb =
        ud?.db_name ||
        cpDbs.find((d) => String(d?.cp_db_id) === String(ud?.cp_db_id))
          ?.db_name ||
        "";
      const haystacks = [
        card?.title,
        card?.user_name,
        card?.address,
        card?.locality,
        resolvedDb,
      ]
        .filter(Boolean)
        .map((s) => String(s).toLowerCase());
      return haystacks.some((h) => h.includes(q));
    });
  }, [active, list, customerSearch, cpDbs]);

  const closeMisroute = useCallback(
    () => setMisroute({ visible: false, card: null }),
    [],
  );

  const handleMisrouteConfirm = useCallback(async () => {
    const card = misroute.card;
    if (!card) return;
    const tps = (Array.isArray(card?.tp_list) ? card.tp_list : []).map(
      (tp) => ({
        cp_id: cpId,
        cx_id: tp?.user_id,
        cp_db_id: null,
        route_id: "MISROUTE",
      }),
    );
    if (!tps.length) {
      closeMisroute();
      return;
    }
    const ok = await submit({
      tps,
      cp_db_id: null,
      route_id: "MISROUTE",
      successMessage: `${tps.length} marked Out of Area`,
    });
    if (ok) closeMisroute();
  }, [misroute, cpId, submit, closeMisroute]);

  const handleUnassignCustomer = useCallback(
    async (card) => {
      const tps = (Array.isArray(card?.tp_list) ? card.tp_list : []).map(
        (tp) => ({
          cp_id: cpId,
          cx_id: tp?.user_id,
          cp_db_id: null,
          route_id: "UNASSIGN",
        }),
      );
      if (!tps.length) return;
      await submit({
        tps,
        cp_db_id: null,
        route_id: "UNASSIGN",
        successMessage: "Unassigned",
      });
    },
    [cpId, submit],
  );

  return (
    <View style={sharedStyles.flex1}>
      <View style={styles.subTabBar}>
        {SUB_TABS.map((t) => {
          const isActive = t.key === active;
          return (
            <TouchableOpacity
              key={t.key}
              activeOpacity={0.85}
              onPress={() => {
                setActive(t.key);
                if (t.key !== "TP") setCustomerSearch("");
              }}
              style={[styles.subTab, isActive && styles.subTabActive]}
            >
              <Text
                variant="caption_2"
                style={[
                  styles.subTabText,
                  isActive
                    ? styles.subTabTextActive
                    : styles.subTabTextInactive,
                ]}
              >
                {t.label}
                {data?.tabs?.[t.key]?.count != null
                  ? ` (${data.tabs[t.key].count})`
                  : ""}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
      {active === "TP" ? (
        <View style={styles.searchBar}>
          <TextInput
            value={customerSearch}
            onChangeText={setCustomerSearch}
            placeholder="Search customer, DB, locality…"
            placeholderTextColor={tokens.textTertiary}
            style={styles.searchInput}
            returnKeyType="search"
            autoCorrect={false}
            autoCapitalize="none"
            testID="customer-search-input"
          />
        </View>
      ) : null}
      <ScrollView
        contentContainerStyle={styles.scroll}
        refreshControl={
          <RefreshControl
            refreshing={loading}
            onRefresh={reload}
            tintColor={color.mediumBlack}
          />
        }
      >
        {active === "TP" ? (
          filteredCustomers.length === 0 ? (
            <Text variant="caption_2" style={sharedStyles.emptyText}>
              {customerSearch.trim()
                ? `No customers match "${customerSearch.trim()}".`
                : "No customers found."}
            </Text>
          ) : (
            filteredCustomers.map((card) => (
              <CustomerCard
                key={String(card?.search_key || card?.title)}
                card={card}
                cpDbs={cpDbs}
                onPressMisroute={(c) => setMisroute({ visible: true, card: c })}
                onPressUnassign={
                  submitting ? undefined : handleUnassignCustomer
                }
              />
            ))
          )
        ) : list.length === 0 ? (
          <Text variant="caption_2" style={sharedStyles.emptyText}>
            No {active === "DC" ? "localities" : "crates"} found.
          </Text>
        ) : active === "DC" ? (
          list.map((card) => (
            <LocalityCard
              key={String(card?.search_key || card?.title)}
              card={card}
              cpDbs={cpDbs}
              onPressViewMap={onPressViewMap}
            />
          ))
        ) : (
          list.map((card) => (
            <CrateCard
              key={String(card?.search_key || card?.title)}
              card={card}
              onPressViewMap={onPressViewMap}
            />
          ))
        )}
      </ScrollView>
      <MisrouteSheet
        visible={misroute.visible}
        card={misroute.card}
        submitting={submitting}
        onClose={closeMisroute}
        onConfirm={handleMisrouteConfirm}
      />
    </View>
  );
};

export default LocalityCrateInfoTab;

const styles = StyleSheet.create({
  subTabBar: {
    flexDirection: "row",
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: tokens.spacing.small,
    backgroundColor: tokens.surface,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: tokens.borderLight,
  },
  subTab: {
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 6,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    borderColor: tokens.borderLight,
    backgroundColor: tokens.surface,
    marginRight: tokens.spacing.small,
  },
  subTabActive: {
    backgroundColor: tokens.pill.preferred.bg,
    borderColor: tokens.pill.preferred.border,
  },
  subTabText: { fontSize: 12 },
  subTabTextActive: { color: tokens.pill.preferred.fg, fontWeight: "700" },
  subTabTextInactive: { color: tokens.textSecondary },
  searchBar: {
    backgroundColor: tokens.surface,
    paddingHorizontal: tokens.spacing.large,
    paddingVertical: tokens.spacing.small,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: tokens.borderLight,
  },
  searchInput: {
    backgroundColor: tokens.surfaceHover,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 8,
    fontSize: 14,
    color: tokens.textPrimary,
  },
  scroll: {
    paddingTop: tokens.spacing.medium,
    paddingBottom: tokens.spacing.xlarge,
  },
});
